import { useNavigate } from "react-router-dom";
import { ChevronRight, AlertTriangle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useInstallments } from "@/lib/useInstallmentsCache";

function fmt(n, ccy) {
  return `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${ccy}`;
}

const GRADIENT = "linear-gradient(90deg, #dc2626 0%, #f97316 35%, #f5b800 60%, #84cc16 82%, #16a34a 100%)";

function statusOf(inst) {
  if (inst.paid_count >= inst.total_installments) return { key: "done", cls: "bg-emerald-500 text-white", label: { en: "Done", ar: "مكتمل" } };
  if (inst.is_overdue) return { key: "overdue", cls: "bg-red-600 text-white animate-pulse", label: { en: "Overdue", ar: "متأخر" } };
  const due = inst.next_due;
  if (due) {
    const d = Math.round((new Date(due) - new Date()) / 864e5);
    if (d <= 3) return { key: "soon", cls: "bg-amber-500 text-white", label: { en: `In ${Math.max(0,d)}d`, ar: `خلال ${Math.max(0,d)} ي` } };
  }
  return { key: "later", cls: "bg-slate-200 text-slate-700", label: { en: "Upcoming", ar: "قادم" } };
}

function ProgressRow({ inst, ccy, t, lang, onPay }) {
  const paid = inst.paid_count || 0;
  const total = inst.total_installments || 1;
  const pct = Math.max(2, Math.min(100, (paid / total) * 100));
  const remainingAmount = (total - paid) * inst.amount;
  const paidAmount = paid * inst.amount;
  const s = statusOf(inst);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-bold text-slate-700 truncate max-w-[45%]">{inst.name}</span>
        <button data-testid={`inst-status-${inst.id}`} onClick={() => onPay(inst)}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold shadow-sm active:scale-95 ${s.cls}`}>
          {s.label[lang] || s.label.en}
        </button>
        <span className="tabular-nums font-semibold text-slate-500">{paid}/{total}</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-slate-200/70 neu-inset overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, background: GRADIENT, backgroundSize: `${(100/pct)*100}% 100%`, backgroundPosition: "left center", boxShadow: "0 1px 3px rgba(0,0,0,0.15) inset", transition: "width 700ms ease" }} />
      </div>
      <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500 tabular-nums">
        <span className="text-emerald-600 font-semibold">{t("paid")}: {fmt(paidAmount, ccy)}</span>
        <span className="text-rose-600 font-semibold">{t("remainingPayments")}: {fmt(remainingAmount, ccy)}</span>
      </div>
    </div>
  );
}

export function NeuActiveInstallments() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const data = useInstallments();
  const ccy = user?.currency || "AED";

  const active = data?.active || [];
  const top = active.slice(0, 4);
  const overdueCount = active.filter((i) => i.is_overdue).length;
  const goPay = (inst) => nav(`/installments?pay=${inst.id}`);

  return (
    <div className="neu p-4 sm:p-5 space-y-4" data-testid="dashboard-installments-widget">
      <button onClick={() => nav("/installments")} className="w-full flex items-center justify-between hover:opacity-90 active:scale-[0.99] transition-all">
        <div className="min-w-0 text-start">
          <p className="font-display text-lg font-bold neu-accent-blue">{t("activeInstallments")}</p>
          <p className="font-display font-extrabold text-base neu-accent-blue">
            {active.length} · {fmt(data?.total_monthly || 0, ccy)}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </button>
      {overdueCount > 0 && (
        <p className="text-xs font-semibold text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {overdueCount} {t("overdue")}
        </p>
      )}
      {top.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-3">{t("noInstallments")}</p>
      ) : (
        <div className="space-y-4">
          {top.map((i) => <ProgressRow key={i.id} inst={i} ccy={ccy} t={t} lang={lang} onPay={goPay} />)}
        </div>
      )}
    </div>
  );
}
